import { useState } from "react";
import { Car, Luggage, Clock, Users, Phone, Mail, ArrowRight, Shield, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const INDIATAXI_BASE = "https://indiataxi.pro/book";

/* <TaxiRequestForm iata="COK" airportName="Cochin International Airport"
                   city="Kochi" />
   Posts the lead to our backend (saved for admin review) and simultaneously
   deep-links the user to indiataxi.pro to complete booking. */
export default function TaxiRequestForm({ iata, airportName, city }) {
  const [form, setForm] = useState({
    direction: "from_airport", // from_airport | to_airport
    destination: "",
    date: "",
    time: "",
    pax: 1,
    vehicle: "sedan",
    name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [state, setState] = useState("idle"); // idle | submitting | done
  const [deepLink, setDeepLink] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.destination || !form.date || !form.time || !form.phone) {
      toast.error("Please fill in destination, date, time and phone");
      return;
    }
    setState("submitting");
    try {
      const payload = { ...form, iata, airport_name: airportName, city };
      const r = await axios.post(`${API}/api/taxi-requests`, payload);
      const link = r.data?.deeplink || buildFallbackDeeplink(payload);
      setDeepLink(link);
      setState("done");
      toast.success("Request saved. Opening IndiaTaxi.pro…");
      // Open indiataxi.pro in a new tab after a short delay
      setTimeout(() => window.open(link, "_blank", "noopener"), 600);
    } catch (err) {
      // Fallback: still deep-link the user even if our backend fails.
      const link = buildFallbackDeeplink({ ...form, iata, airport_name: airportName, city });
      setDeepLink(link);
      setState("done");
      toast.warning("Couldn't save locally — redirecting to IndiaTaxi.pro to finish booking");
      setTimeout(() => window.open(link, "_blank", "noopener"), 600);
    }
  };

  if (state === "done") {
    return (
      <aside data-testid="taxi-form-done" className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 sm:p-6">
        <div className="flex items-center gap-2 text-emerald-800 font-900">
          <CheckCircle2 className="w-5 h-5" /> Request submitted
        </div>
        <p className="mt-2 text-sm text-emerald-900 leading-relaxed">
          We've logged your taxi request for <b>{airportName} ({iata})</b>. A tab opened to IndiaTaxi.pro — complete the booking there to confirm your driver.
        </p>
        <a
          href={deepLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="taxi-form-redirect"
          className="mt-3 inline-flex items-center gap-1.5 bg-emerald-700 text-white rounded-full px-4 py-2 text-sm font-800 hover:bg-emerald-800"
        >
          Open IndiaTaxi.pro <ArrowRight className="w-4 h-4" />
        </a>
        <button
          onClick={() => setState("idle")}
          data-testid="taxi-form-new"
          className="ml-3 inline-flex items-center gap-1 text-emerald-800 font-700 text-sm hover:underline"
        >
          Request another ride →
        </button>
      </aside>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="taxi-form"
      className="rounded-3xl border border-zinc-200 bg-white p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex p-2.5 rounded-xl bg-amber-100 text-amber-700"><Car className="w-5 h-5" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-900 text-xl text-zinc-900">Book an airport taxi</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Prepaid, driver-tracked — fulfilled by <a href="https://indiataxi.pro" target="_blank" rel="noopener noreferrer" className="font-800 text-blue-700 hover:underline notranslate">IndiaTaxi.pro</a></p>
        </div>
      </div>

      {/* Direction toggle */}
      <div className="mt-4 inline-flex rounded-full border border-zinc-300 bg-zinc-50 p-1 text-xs font-800" data-testid="taxi-direction">
        {[
          ["from_airport", `From ${iata}`],
          ["to_airport", `To ${iata}`],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => upd("direction", k)}
            data-testid={`taxi-dir-${k}`}
            className={`px-3 py-1 rounded-full transition-colors ${
              form.direction === k ? "bg-[#0A2A5E] text-white" : "text-zinc-600 hover:text-blue-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        <Field label={form.direction === "from_airport" ? "Drop address" : "Pickup address"} icon={Luggage}>
          <input
            value={form.destination}
            onChange={(e) => upd("destination", e.target.value)}
            required
            data-testid="taxi-destination"
            placeholder={form.direction === "from_airport" ? "Hotel name, locality or full address" : "Hotel, home or pickup landmark"}
            className="input"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date" icon={Clock}>
            <input type="date" value={form.date} onChange={(e) => upd("date", e.target.value)} required data-testid="taxi-date" className="input" />
          </Field>
          <Field label="Time" icon={Clock}>
            <input type="time" value={form.time} onChange={(e) => upd("time", e.target.value)} required data-testid="taxi-time" className="input" />
          </Field>
        </div>
        <Field label="Passengers" icon={Users}>
          <select value={form.pax} onChange={(e) => upd("pax", Number(e.target.value))} data-testid="taxi-pax" className="input">
            {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} passenger{n > 1 ? "s" : ""}</option>)}
          </select>
        </Field>
        <Field label="Vehicle" icon={Car}>
          <select value={form.vehicle} onChange={(e) => upd("vehicle", e.target.value)} data-testid="taxi-vehicle" className="input">
            <option value="sedan">Sedan (4 pax, 2 bags)</option>
            <option value="suv">SUV / Innova (6 pax, 4 bags)</option>
            <option value="hatchback">Hatchback (3 pax, 1 bag)</option>
            <option value="premium">Premium / Camry (3 pax, 3 bags)</option>
          </select>
        </Field>
        <Field label="Your name" icon={Users}>
          <input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="taxi-name" placeholder="Full name" className="input" />
        </Field>
        <Field label="Phone (required)" icon={Phone}>
          <input value={form.phone} onChange={(e) => upd("phone", e.target.value)} required data-testid="taxi-phone" placeholder="+91 98xxxxxxxx" className="input" />
        </Field>
        <Field label="Email (optional)" icon={Mail}>
          <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="taxi-email" placeholder="you@example.com" className="input" />
        </Field>
        <Field label="Notes (optional)">
          <input value={form.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="taxi-notes" placeholder="Flight number, extra stop, child seat…" className="input" />
        </Field>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          type="submit"
          disabled={state === "submitting"}
          data-testid="taxi-submit"
          className="inline-flex items-center gap-1.5 bg-[#0A2A5E] text-white rounded-full px-5 py-2.5 text-sm font-800 hover:bg-blue-700 disabled:opacity-60"
        >
          {state === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />}
          {state === "submitting" ? "Submitting…" : "Request taxi"}
        </button>
        <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          We save your request locally, then open IndiaTaxi.pro to confirm the fare and driver.
        </span>
      </div>

      <style>{`.input { width: 100%; border: 1px solid #d4d4d8; border-radius: 10px; padding: 8px 10px; font-size: 13px; } .input:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15) }`}</style>
    </form>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-800 uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </span>
      {children}
    </label>
  );
}

function buildFallbackDeeplink(p) {
  const params = new URLSearchParams({
    source: "indianairports.com",
    airport: p.iata || "",
    airport_name: p.airport_name || "",
    city: p.city || "",
    direction: p.direction,
    destination: p.destination,
    date: p.date,
    time: p.time,
    pax: String(p.pax),
    vehicle: p.vehicle,
    ...(p.name ? { name: p.name } : {}),
    ...(p.phone ? { phone: p.phone } : {}),
    ...(p.email ? { email: p.email } : {}),
  });
  return `${INDIATAXI_BASE}?${params.toString()}`;
}
